import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { authApi } from '@/api/auth';
import { useAuthStore } from '@/stores/auth';

const HEARTBEAT_THROTTLE_MS = 3000; // interval minim între 2 heartbeat-uri
const WARN_THRESHOLD_SEC = 60;

interface Props {
  /**
   * Mod de rendering. `compact` → doar MM:SS (pentru spațiu mic, ex mobile).
   * `full` → iconă + MM:SS.
   */
  variant?: 'compact' | 'full';
}

/**
 * Counter sesiune + activity tracker. Afișează timpul rămas până la expirare
 * și trimite heartbeat-uri la server (cu throttle 3s) la mouse move / click /
 * keydown ca să re-extindă rolling session-ul backend.
 *
 * La expirare (<= 0) cheamă logout() și redirecționează la /login.
 */
export default function SessionTimer({ variant = 'full' }: Props) {
  const { t } = useTranslation();
  const expiresAt = useAuthStore((s) => s.expiresAt);
  const extendSession = useAuthStore((s) => s.extendSession);
  const logout = useAuthStore((s) => s.logout);
  const isAuthenticated = useAuthStore((s) => s.user !== null);
  const navigate = useNavigate();

  const [remaining, setRemaining] = useState<number>(() =>
    computeRemaining(expiresAt),
  );
  const expiredRef = useRef(false);

  // Recomputăm la fiecare secundă.
  useEffect(() => {
    if (!expiresAt) {
      setRemaining(0);
      return;
    }
    setRemaining(computeRemaining(expiresAt));
    const id = window.setInterval(() => {
      setRemaining(computeRemaining(expiresAt));
    }, 1000);
    return () => window.clearInterval(id);
  }, [expiresAt]);

  // Auto-logout la expirare.
  useEffect(() => {
    if (!isAuthenticated || expiredRef.current) return;
    if (remaining <= 0 && expiresAt) {
      expiredRef.current = true;
      (async () => {
        await logout();
        navigate('/login?notice=session_expired', { replace: true });
      })();
    }
  }, [remaining, expiresAt, isAuthenticated, logout, navigate]);

  // Activity tracker — mouse/click/keydown cu throttle.
  const lastHeartbeatRef = useRef<number>(0);
  const inflightRef = useRef<boolean>(false);

  const sendHeartbeat = useCallback(async () => {
    if (inflightRef.current) return;
    inflightRef.current = true;
    try {
      const resp = await authApi.heartbeat();
      extendSession(resp.expires_at);
    } catch {
      // 401 / network — ignorăm. Dacă sesiunea chiar a picat server-side,
      // următorul countdown tick + orice API call va trigger flow-ul de
      // logout. Nu forțăm logout din heartbeat failure singur.
    } finally {
      inflightRef.current = false;
    }
  }, [extendSession]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const onActivity = () => {
      const now = Date.now();
      if (now - lastHeartbeatRef.current < HEARTBEAT_THROTTLE_MS) return;
      lastHeartbeatRef.current = now;
      void sendHeartbeat();
    };
    // passive: true pe mousemove — nu blocăm scroll-ul.
    window.addEventListener('mousemove', onActivity, { passive: true });
    window.addEventListener('click', onActivity, { passive: true });
    window.addEventListener('keydown', onActivity, { passive: true });
    window.addEventListener('touchstart', onActivity, { passive: true });
    return () => {
      window.removeEventListener('mousemove', onActivity);
      window.removeEventListener('click', onActivity);
      window.removeEventListener('keydown', onActivity);
      window.removeEventListener('touchstart', onActivity);
    };
  }, [isAuthenticated, sendHeartbeat]);

  if (!isAuthenticated || !expiresAt) return null;

  const text = formatRemaining(Math.max(0, remaining));
  const warn = remaining <= WARN_THRESHOLD_SEC;
  const color = warn ? 'var(--c-rose)' : 'var(--text-muted)';

  if (variant === 'compact') {
    return (
      <span
        className="font-mono tabular-nums text-[11px]"
        style={{ color }}
        aria-label={t('shell_ui.session_timer.aria_label', { time: text })}
        title={t('shell_ui.session_timer.title_compact', { time: text })}
      >
        {text}
      </span>
    );
  }

  return (
    <div
      className="inline-flex items-center gap-1.5 font-mono tabular-nums text-[12px]"
      style={{
        color,
        padding: '4px 10px',
        borderRadius: 6,
        border: `1px solid ${warn ? 'var(--c-rose)' : 'var(--border-strong)'}`,
        background: warn ? 'var(--bg-rose)' : 'var(--bg)',
      }}
      title={t('shell_ui.session_timer.title_full', { time: text })}
    >
      <ClockIcon className="w-3.5 h-3.5" />
      <span>{text}</span>
    </div>
  );
}

function computeRemaining(expiresAt: Date | null): number {
  if (!expiresAt) return 0;
  return Math.max(0, Math.floor((expiresAt.getTime() - Date.now()) / 1000));
}

function formatRemaining(sec: number): string {
  // Ultimul minut — arătăm secundele, acolo precizia contează.
  if (sec < 60) return `${sec}s`;
  // Peste o oră — arătăm h + m (rotunjim minutele la ceiling ca să nu pice
  // brusc de la „2h 0m" la „59m").
  if (sec >= 3600) {
    const h = Math.floor(sec / 3600);
    const m = Math.ceil((sec % 3600) / 60);
    if (m === 60) return `${h + 1}h 0m`;
    return `${h}h ${m}m`;
  }
  // 1–59 min — arătăm minutele (ceiling, ca counter-ul să afișeze „15m"
  // pentru valori între 14:01 și 15:00).
  return `${Math.ceil(sec / 60)}m`;
}

function ClockIcon({ className = '' }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <polyline points="12,7 12,12 15,14" />
    </svg>
  );
}
